import { useState, type FormEvent } from 'react';
import {
  DEFAULT_SETTINGS,
  SETTINGS_BOUNDS,
  type SystemSettings,
  type UpdateSettingsInput,
} from '@orbit-sync/shared-types';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { FormBanner } from '../../components/FormBanner.js';
import { Icon } from '../../components/Icon.js';
import { api, errorMessage, fieldErrorsFrom, type FieldErrors } from '../../lib/api/index.js';
import { useSettingsStore } from '../../settings/settingsStore.js';
import { useAdminQuery } from '../useAdminQuery.js';

const MB = 1024 * 1024;

type BooleanKey = {
  [K in keyof SystemSettings]: SystemSettings[K] extends boolean ? K : never;
}[keyof SystemSettings];

type NumberKey = {
  [K in keyof SystemSettings]: SystemSettings[K] extends number ? K : never;
}[keyof SystemSettings];

const HOURS_PER_DAY = 24;

/** "30 days", "36 hours", "1 hour". */
function describeHours(hours: number): string {
  if (hours >= HOURS_PER_DAY && hours % HOURS_PER_DAY === 0) {
    const days = hours / HOURS_PER_DAY;
    return `${days} day${days === 1 ? '' : 's'}`;
  }

  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

function parseDomains(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\s,;]+/)
        .map((domain) => domain.trim().toLowerCase().replace(/^@/, ''))
        .filter(Boolean),
    ),
  ];
}

/** System settings: password policy, limits and feature switches. */
export function AdminSettingsPage() {
  const { data, error, reload } = useAdminQuery(
    (signal) => api.admin.getSettings(signal),
    'settings',
  );

  return (
    <section className="admin-page">
      <header className="admin-head">
        <div>
          <h1>Settings</h1>
          <p className="muted small">
            Enforced by the server for everyone, and pushed to connected clients the moment you
            save.
          </p>
        </div>
      </header>
      <FormBanner message={error} />
      {/* Keyed by the loaded values, so the form starts from what is in force. */}
      {data ? <SettingsForm key={JSON.stringify(data)} initial={data} /> : null}
      <ConfigAndBackup onImported={reload} />
      <SignOutEveryone />
    </section>
  );
}

/**
 * Export / import the configuration as a file, and download a database backup.
 * The file carries settings and the directory configuration but never the
 * directory's service-account password.
 */
function ConfigAndBackup({ onImported }: { onImported: () => void }) {
  const [busy, setBusy] = useState<'export' | 'import' | 'backup' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function run(kind: 'export' | 'import' | 'backup', work: () => Promise<string>) {
    setBusy(kind);
    setError(null);
    setNotice(null);

    try {
      setNotice(await work());
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(null);
    }
  }

  const exportFile = () =>
    run('export', async () => {
      const file = await api.admin.exportConfig();
      const stamp = (file.exportedAt ?? new Date().toISOString()).slice(0, 10);
      api.admin.saveBlob(
        new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' }),
        `orbit-sync-config-${stamp}.json`,
      );
      return 'Configuration exported.';
    });

  const importFile = (file: File) =>
    run('import', async () => {
      let parsed: unknown;

      try {
        parsed = JSON.parse(await file.text());
      } catch {
        throw new Error('That file is not an Orbit Sync configuration (not valid JSON).');
      }

      const result = await api.admin.importConfig(parsed);
      onImported();
      const parts = [
        result.settingsImported ? 'settings' : null,
        result.directoryImported ? 'directory configuration' : null,
      ].filter(Boolean);
      return parts.length > 0 ? `Imported ${parts.join(' and ')}.` : 'Nothing to import.';
    });

  const backup = () =>
    run('backup', async () => {
      await api.admin.downloadBackup();
      return 'Backup downloaded.';
    });

  return (
    <fieldset className="admin-panel">
      <legend>Configuration and backup</legend>
      <FormBanner message={error} />
      {notice ? (
        <p className="field-hint" role="status">
          <Icon name="check" size={13} /> {notice}
        </p>
      ) : null}
      <div className="admin-danger-row">
        <div>
          <strong>Configuration file</strong>
          <p className="field-hint">
            System settings and the directory (LDAP) configuration as JSON - to keep a copy or move
            it to another server. The directory&apos;s service-account password is not included; an
            import keeps the one already saved here.
          </p>
        </div>
        <div className="ldap-actions">
          <button
            type="button"
            className="btn btn--secondary"
            disabled={busy !== null}
            onClick={() => void exportFile()}
          >
            {busy === 'export' ? (
              <span className="btn-spinner" />
            ) : (
              <Icon name="download" size={14} />
            )}
            Export
          </button>
          <label className="btn btn--secondary" aria-disabled={busy !== null}>
            {busy === 'import' ? (
              <span className="btn-spinner" />
            ) : (
              <Icon name="upload" size={14} />
            )}
            Import…
            <input
              type="file"
              accept="application/json,.json"
              hidden
              disabled={busy !== null}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';

                if (file) {
                  void importFile(file);
                }
              }}
            />
          </label>
        </div>
      </div>
      <div className="admin-danger-row">
        <div>
          <strong>Database backup</strong>
          <p className="field-hint">
            A full PostgreSQL dump (accounts, conversations, settings) - shared files are never in
            the database. Restore with{' '}
            <code>pg_restore --clean --if-exists -d orbit_sync backup.dump</code>.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--secondary"
          disabled={busy !== null}
          onClick={() => void backup()}
        >
          {busy === 'backup' ? (
            <span className="btn-spinner" />
          ) : (
            <Icon name="database" size={14} />
          )}
          Download backup
        </button>
      </div>
    </fieldset>
  );
}

/** The emergency control: every user, every device, signed out now. */
function SignOutEveryone() {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      const { revoked } = await api.admin.endAllSessions();
      setDone(revoked);
      setConfirming(false);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <fieldset className="admin-panel">
      <legend>Emergency</legend>
      <div className="admin-danger-row">
        <div>
          <strong>Sign everyone out</strong>
          <p className="field-hint">
            Ends every session on every device at once - for a suspected breach, or after changing
            the session rules. Everyone signs in again. Your administrator session is not affected.
          </p>
          {done !== null ? (
            <p className="admin-notice">
              <Icon name="check" size={14} />
              {done} session{done === 1 ? '' : 's'} ended
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="btn btn--danger"
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
        >
          <Icon name="logOut" size={14} />
          Sign everyone out
        </button>
      </div>
      {confirming ? (
        <ConfirmDialog
          title="Sign every user out?"
          confirmLabel="Sign everyone out"
          requireText="SIGN OUT"
          busy={busy}
          error={error}
          onConfirm={() => void confirm()}
          onCancel={() => setConfirming(false)}
        >
          <p>Every signed-in user, on every device, is signed out immediately.</p>
        </ConfirmDialog>
      ) : null}
    </fieldset>
  );
}

function SettingsForm({ initial }: { initial: SystemSettings }) {
  const applyLocally = useSettingsStore((state) => state.apply);
  const [saved, setSaved] = useState(initial);
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [justSaved, setJustSaved] = useState(false);

  const [domainsText, setDomainsText] = useState(initial.allowedEmailDomains.join(', '));
  const changes = Object.fromEntries(
    (Object.keys(draft) as (keyof SystemSettings)[])
      .filter((key) => JSON.stringify(draft[key]) !== JSON.stringify(saved[key]))
      .map((key) => [key, draft[key]]),
  ) as UpdateSettingsInput;
  const dirty = Object.keys(changes).length > 0;

  const set = <K extends keyof SystemSettings>(key: K, value: SystemSettings[K]): void => {
    setDraft((current) => ({ ...current, [key]: value }));
    setJustSaved(false);
  };

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    setBusy(true);
    setError(null);
    setFieldErrors({});

    try {
      const next = await api.admin.updateSettings(changes);
      setSaved(next);
      setDraft(next);
      setDomainsText(next.allowedEmailDomains.join(', '));
      // This tab is the admin's, not necessarily a signed-in chat client, so
      // the socket broadcast may never reach it.
      applyLocally(next);
      setJustSaved(true);
    } catch (cause) {
      setFieldErrors(fieldErrorsFrom(cause));
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  const toggle = (key: BooleanKey, label: string, hint: string) => (
    <label className="setting-toggle">
      <input
        type="checkbox"
        checked={draft[key]}
        onChange={(event) => set(key, event.target.checked)}
      />
      <span className="switch" aria-hidden="true" />
      <span>
        <strong>{label}</strong>
        <span className="field-hint">{hint}</span>
      </span>
    </label>
  );

  const number = (key: NumberKey, label: string, hint: string) => {
    const bounds = SETTINGS_BOUNDS[key as keyof typeof SETTINGS_BOUNDS];

    return (
      <div className="field">
        <label htmlFor={`setting-${key}`}>{label}</label>
        <input
          id={`setting-${key}`}
          type="number"
          min={bounds.min}
          max={bounds.max}
          value={draft[key]}
          onChange={(event) => set(key, Number(event.target.value))}
          aria-invalid={fieldErrors[key] ? true : undefined}
        />
        <p className="field-hint">
          {hint} Allowed {bounds.min}–{bounds.max}.
        </p>
        {fieldErrors[key] ? <p className="field-error">{fieldErrors[key]}</p> : null}
      </div>
    );
  };

  const transferBounds = SETTINGS_BOUNDS.maxFileTransferBytes;

  return (
    <form className="settings-form" onSubmit={(event) => void submit(event)} noValidate>
      <FormBanner message={error} />

      <fieldset className="admin-panel">
        <legend>Accounts</legend>
        {toggle(
          'allowRegistration',
          'Allow sign-up',
          'When off, only existing accounts (and directory users) can sign in.',
        )}
        <div className="field">
          <label htmlFor="setting-domains">Allowed sign-up email domains</label>
          <input
            id="setting-domains"
            value={domainsText}
            placeholder="example.com, corp.example.com"
            onChange={(event) => {
              setDomainsText(event.target.value);
              set('allowedEmailDomains', parseDomains(event.target.value));
            }}
            aria-invalid={fieldErrors.allowedEmailDomains ? true : undefined}
          />
          <p className="field-hint">
            Comma-separated. When set, self-service sign-up only accepts these domains - without it,
            anyone who can reach the server can create an account. Empty allows any.
          </p>
          {Object.entries(fieldErrors)
            .filter(([key]) => key.startsWith('allowedEmailDomains'))
            .map(([key, message]) => (
              <p key={key} className="field-error">
                {message}
              </p>
            ))}
        </div>
      </fieldset>

      <fieldset className="admin-panel">
        <legend>Sessions and sign-in</legend>
        {number(
          'sessionMaxLifetimeHours',
          'Maximum session length (hours)',
          `However active someone is, they sign in again this long after signing in - now ${describeHours(draft.sessionMaxLifetimeHours)}. Applies to existing sessions immediately. 720 is 30 days, 2160 is 90 days, 8760 is a year.`,
        )}
        {number(
          'sessionIdleTimeoutHours',
          'Sign out after inactivity (hours)',
          `A session not used for this long ends early. ${draft.sessionIdleTimeoutHours === 0 ? 'Now off.' : `Now ${describeHours(draft.sessionIdleTimeoutHours)}.`} 0 turns it off.`,
        )}
        {number(
          'loginMaxFailedAttempts',
          'Lock an account after failed sign-ins',
          'Wrong passwords in a row before the account is locked for a while. 0 turns lockout off.',
        )}
        {number(
          'loginLockoutMinutes',
          'Lockout length (minutes)',
          'How long a locked account stays locked. An administrator can unlock it sooner from Users.',
        )}
      </fieldset>

      <fieldset className="admin-panel">
        <legend>Password policy</legend>
        {number('passwordMinLength', 'Minimum length', 'Characters, counted as people see them.')}
        {toggle(
          'passwordRequireUppercase',
          'Require an uppercase letter',
          'A–Z, or any cased script.',
        )}
        {toggle(
          'passwordRequireLowercase',
          'Require a lowercase letter',
          'a–z, or any cased script.',
        )}
        {toggle('passwordRequireDigit', 'Require a digit', 'Persian and Arabic digits count.')}
        {toggle(
          'passwordRequireSymbol',
          'Require a symbol',
          'Anything that is not a letter, digit or space.',
        )}
        <p className="field-hint">Applies to new passwords. Existing passwords keep working.</p>
      </fieldset>

      <fieldset className="admin-panel">
        <legend>Limits</legend>
        {number('maxMessageLength', 'Maximum message length', 'Characters per message.')}
        {number('maxDisplayNameLength', 'Maximum display name length', 'Characters.')}
        {number('maxChannelNameLength', 'Maximum group name length', 'Characters.')}
        <div className="field">
          <label htmlFor="setting-transfer">Maximum file size (MB)</label>
          <input
            id="setting-transfer"
            type="number"
            min={transferBounds.min / MB}
            max={transferBounds.max / MB}
            value={Math.round(draft.maxFileTransferBytes / MB)}
            onChange={(event) =>
              set('maxFileTransferBytes', Math.round(Number(event.target.value) * MB))
            }
          />
          <p className="field-hint">
            Shared files are held in the server&apos;s memory until downloaded, so very large limits
            cost server RAM. Allowed {transferBounds.min / MB}–{transferBounds.max / MB} MB.
          </p>
          {fieldErrors.maxFileTransferBytes ? (
            <p className="field-error">{fieldErrors.maxFileTransferBytes}</p>
          ) : null}
        </div>
        {number(
          'fileRetentionMinutes',
          'Keep undownloaded files for (minutes)',
          'After this a shared file is dropped from memory even if not everyone has downloaded it.',
        )}
      </fieldset>

      <fieldset className="admin-panel">
        <legend>Messages</legend>
        {toggle(
          'allowMessageEditing',
          'Allow editing sent messages',
          'People can change the text of their own messages.',
        )}
        {toggle(
          'allowMessageDeletion',
          'Allow deleting sent messages',
          'People can delete their own messages for everyone.',
        )}
        {toggle(
          'allowDeleteChatForEveryone',
          'Allow deleting a direct chat for both people',
          'Either person can delete the whole conversation, history and files, for both of them.',
        )}
      </fieldset>

      <div className="settings-actions">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => {
            setDraft(DEFAULT_SETTINGS);
            setDomainsText(DEFAULT_SETTINGS.allowedEmailDomains.join(', '));
            setJustSaved(false);
          }}
        >
          <Icon name="rotate" size={14} />
          Restore defaults
        </button>
        {justSaved && !dirty ? (
          <span className="admin-notice">
            <Icon name="check" size={14} />
            Saved
          </span>
        ) : null}
        <button type="submit" className="btn btn--primary" disabled={!dirty || busy}>
          {busy ? <span className="btn-spinner" /> : <Icon name="check" size={14} />}
          Save settings
        </button>
      </div>
    </form>
  );
}
